// components/ui/tabs.tsx
"use client";

import React, { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface TabsProps {
  defaultValue?: string;
  value?: string;
  className?: string;
  children: React.ReactNode;
  onChange?: (value: string) => void;
}

interface TabsListProps {
  className?: string;
  children: React.ReactNode;
}

interface TabsTriggerProps {
  value: string;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

interface TabsContentProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

const TabsContext = React.createContext<{
  value: string;
  onValueChange: (value: string) => void;
}>({
  value: "",
  onValueChange: () => {},
});

export function Tabs({
  defaultValue,
  value,
  className,
  children,
  onChange,
}: TabsProps) {
  const [internalValue, setInternalValue] = useState(defaultValue || value || "");

  // Si value est fourni (mode contrôlé), on l'utilise
  // Sinon on utilise l'état interne (mode non-contrôlé)
  const currentValue = value !== undefined ? value : internalValue;

  // Synchroniser l'état interne quand defaultValue change (mode non-contrôlé seulement)
  useEffect(() => {
    if (value === undefined && defaultValue !== undefined) {
      setInternalValue(defaultValue);
    }
  }, [defaultValue, value]);

  const handleValueChange = (newValue: string) => {
    // En mode non-contrôlé, mettre à jour l'état interne
    if (value === undefined) {
      setInternalValue(newValue);
    }
    // Toujours appeler le callback onChange si fourni
    onChange?.(newValue);
  };

  return (
    <TabsContext.Provider value={{ value: currentValue, onValueChange: handleValueChange }}>
      <div className={cn("w-full", className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({ className, children }: TabsListProps) {
  return <div className={cn("flex items-center", className)}>{children}</div>;
}

export function TabsTrigger({
  value,
  className,
  children,
  onClick,
  disabled,
}: TabsTriggerProps) {
  const { value: current, onValueChange } = React.useContext(TabsContext);
  const isActive = current === value;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      data-state={isActive ? "active" : "inactive"}
      style={{ outline: "none" }}
      className={cn(
        "px-4 py-2 text-sm font-medium transition-all focus:outline-hidden focus-visible:outline-hidden focus:ring-0 active:outline-hidden active:border-0",
        isActive
          ? "border-b-2 border-primary text-primary"
          : "text-muted-foreground hover:text-foreground",
        className
      )}
      disabled={disabled}
      onClick={() => {
        onValueChange(value);
        onClick?.();
      }}
    >
      {children}
    </button>
  );
}

export function TabsContent({
  value,
  className,
  children,
}: TabsContentProps) {
  const { value: current } = React.useContext(TabsContext);
  if (current !== value) return null;
  return (
    <div role="tabpanel" className={cn("mt-2", className)}>
      {children}
    </div>
  );
}
